/**
 * MoistureSparkline — tiny SVG line chart showing the last N hours of
 * moisture readings for ONE soil channel, with optional threshold lines
 * drawn across so the user sees *why* a rule tripped.
 *
 * Used in two places:
 *   1. MoistureAlertRulesPage — one per rule, so the user can spot which
 *      zone is bouncing along the dry floor.
 *   2. WaterNowSheet — opens after a push tap; the 3-second "before"
 *      receipt that shows the curve crossing the action threshold.
 *
 * Data source: GET /api/dashboard/history-by-channel?metric=moisture&hours=24
 * (already returns per-channel series; we just filter to ours).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../config/api';

const PAD_X = 4;
const PAD_Y = 6;

function buildPath(points, w, h, min, max) {
  if (!points.length) return '';
  const span = Math.max(0.001, max - min);
  const stepX = (w - PAD_X * 2) / Math.max(1, points.length - 1);
  return points
    .map((p, i) => {
      const x = PAD_X + stepX * i;
      const y = h - PAD_Y - ((p.v - min) / span) * (h - PAD_Y * 2);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

export function MoistureSparkline({
  channel,
  hours = 24,
  width = 160,
  height = 44,
  headsUp,
  action,
  testid,
}) {
  const [series, setSeries] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    fetch(
      `${API_URL}/api/dashboard/history-by-channel?metric=moisture&hours=${hours}`,
      { credentials: 'include' }
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancel) return;
        const match = (data.channels || []).find(
          (c) => Number(c.channel) === Number(channel)
        );
        setSeries(match ? match.series : []);
      })
      .catch((e) => !cancel && setErr(e.message));
    return () => {
      cancel = true;
    };
  }, [channel, hours]);

  const { min, max, last } = useMemo(() => {
    if (!series || !series.length) return { min: 0, max: 100, last: null };
    const vals = series.map((s) => s.v);
    const lo = Math.min(...vals, headsUp ?? 100, action ?? 100);
    const hi = Math.max(...vals, headsUp ?? 0, action ?? 0);
    // Pad so we don't draw on the edges
    return { min: lo - 2, max: hi + 2, last: vals[vals.length - 1] };
  }, [series, headsUp, action]);

  if (err) {
    return (
      <div
        data-testid={testid}
        className="text-[10px] text-rose-400/70"
        style={{ width, height }}
      >
        chart: {err}
      </div>
    );
  }
  if (!series) {
    return (
      <div
        data-testid={testid}
        className="bg-slate-950/60 rounded animate-pulse"
        style={{ width, height }}
      />
    );
  }
  if (!series.length) {
    return (
      <div
        data-testid={testid}
        className="text-[10px] text-slate-500 flex items-center justify-center bg-slate-950/40 rounded"
        style={{ width, height }}
      >
        No readings yet
      </div>
    );
  }

  const span = Math.max(0.001, max - min);
  const yFor = (v) => height - PAD_Y - ((v - min) / span) * (height - PAD_Y * 2);
  const path = buildPath(series, width, height, min, max);
  const lastY = last != null ? yFor(last) : null;
  const lastX = width - PAD_X;

  // Color the trace by where the latest reading sits
  const traceColor =
    last == null
      ? '#64748b'
      : action != null && last <= action
        ? '#fb7185' // rose-400 — below action
        : headsUp != null && last <= headsUp
          ? '#fbbf24' // amber-400 — heads up
          : '#34d399'; // emerald-400 — healthy

  return (
    <svg
      data-testid={testid}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block"
      aria-label={`Moisture sparkline, channel ${channel}`}
    >
      {/* Threshold lines */}
      {headsUp != null && (
        <line
          x1={PAD_X}
          x2={width - PAD_X}
          y1={yFor(headsUp)}
          y2={yFor(headsUp)}
          stroke="#fbbf24"
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.5"
        />
      )}
      {action != null && (
        <line
          x1={PAD_X}
          x2={width - PAD_X}
          y1={yFor(action)}
          y2={yFor(action)}
          stroke="#fb7185"
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.7"
        />
      )}
      {/* The trace */}
      <path
        d={path}
        fill="none"
        stroke={traceColor}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Last-point dot */}
      {lastY != null && (
        <circle cx={lastX} cy={lastY} r="2.4" fill={traceColor} />
      )}
    </svg>
  );
}
